import {
	type CartHandoff,
	cartEventItem,
	cartEventType,
	type EmbedTransport,
	type HostToPage,
	hostToPageSchema,
	originMatches,
	type PageToHost,
} from "@repo/embed";
import { debugLog } from "./debug";
import type { BasketChange } from "./types";

/**
 * The bridge between the hosted page and the app around it (ADR-5.4): envelope v1 over the
 * transport the `embed` param names, else the first that exists. Webviews get a JSON string
 * through their native handler; iframes get the object through postMessage to one exact
 * origin, never "*".
 */

/** A `message` listener on the HephonBridge channel object (webview_flutter, Android). */
export type ChannelListener = (event: { data: unknown }) => void;

declare global {
	interface Window {
		flutter_inappwebview?: {
			callHandler: (name: string, ...args: unknown[]) => Promise<unknown>;
		};
		/**
		 * A webview_flutter JavaScript channel, or on Android the object
		 * `addWebMessageListener` injects: it also delivers the host's replies as `message`
		 * events.
		 */
		HephonBridge?: {
			postMessage: (message: string) => void;
			addEventListener?: (
				type: "message",
				listener: ChannelListener,
			) => void;
			removeEventListener?: (
				type: "message",
				listener: ChannelListener,
			) => void;
		};
		ReactNativeWebView?: { postMessage: (message: string) => void };
		/** WKWebView: the iOS SDK's script message handler. */
		webkit?: {
			messageHandlers?: {
				HephonBridge?: { postMessage: (message: string) => void };
			};
		};
		/** Hosts deliver HostToPage messages here (webviews call it, iframes post). */
		hephon?: { receive: (message: unknown) => void };
	}
}

export type PageMessageType = PageToHost["type"];
export type PageMessageData<T extends PageMessageType> = Extract<
	PageToHost,
	{ type: T }
>["data"];

/** What the page can take from its host; sent in `ready`. */
export const BRIDGE_CAPABILITIES = [
	"cart.snapshot",
	"session.launch",
	"theme",
	"focus",
	// Since SDK 1.1.0: an SDK keeps a hidden chat only when the page says it takes this.
	"visibility",
];

export function envelope<T extends PageMessageType>(
	type: T,
	data: PageMessageData<T>,
	id: string = crypto.randomUUID(),
): PageToHost {
	return { v: 1, id, type, ts: Date.now(), data } as PageToHost;
}

function originOf(url: string): string | null {
	try {
		return url ? new URL(url).origin : null;
	} catch {
		return null;
	}
}

/**
 * Who framed us, when we may talk to them: the first of the ancestor origin, the referrer's
 * origin and the `parentOrigin` param that is one of the retailer's allowed origins (or our
 * own, for same-origin test hosts). Null when not framed or nobody qualifies: then nothing is
 * posted and nothing is accepted.
 */
export function resolveEmbedOrigin(
	allowedOrigins: readonly string[],
	parentOrigin: string | undefined,
): string | null {
	if (window.parent === window) {
		return null;
	}
	const own = window.location.origin;
	const candidates = [
		window.location.ancestorOrigins?.[0],
		originOf(document.referrer),
		parentOrigin && originOf(parentOrigin),
	];
	for (const candidate of candidates) {
		if (
			candidate &&
			(candidate === own ||
				allowedOrigins.some((pattern) =>
					originMatches(candidate, pattern),
				))
		) {
			return candidate;
		}
	}
	return null;
}

type Send = (message: object, json: string) => void;

/**
 * `window.HephonBridge`: a webview_flutter JavaScript channel, or on Android the object
 * `addWebMessageListener` injects on the allowed origins.
 */
function channelTransport(): Send | null {
	const channel = window.HephonBridge;
	return channel?.postMessage ? (_, json) => channel.postMessage(json) : null;
}

/** Flutter: flutter_inappwebview's handler, else a webview_flutter JavaScript channel. */
function flutterTransport(): Send | null {
	const webview = window.flutter_inappwebview;
	if (webview?.callHandler) {
		return (_, json) =>
			void webview.callHandler("HephonBridge", json).catch(() => {});
	}
	return channelTransport();
}

function reactNativeTransport(): Send | null {
	const webview = window.ReactNativeWebView;
	return webview ? (_, json) => webview.postMessage(json) : null;
}

/** iOS: the WKScriptMessageHandler the SDK adds before the first load. */
function iosTransport(): Send | null {
	const handler = window.webkit?.messageHandlers?.HephonBridge;
	return handler ? (_, json) => handler.postMessage(json) : null;
}

/** The parent page, at its exact origin; `string` posts the JSON text instead of the object. */
function parentTransport(
	embedOrigin: string | null,
	string: boolean,
): Send | null {
	return window.parent !== window && embedOrigin
		? (message, json) =>
				window.parent.postMessage(string ? json : message, embedOrigin)
		: null;
}

/**
 * The transport `embed` names, so an iframe inside a Flutter webview (whose bridge the plugin
 * also injects into frames) still talks to its parent page. Without `embed`, the first that
 * exists. Null when there is no host to receive.
 */
function transport(
	embed: EmbedTransport | undefined,
	embedOrigin: string | null,
	string = false,
): Send | null {
	switch (embed) {
		case "flutter":
			return flutterTransport();
		case "react-native":
			return reactNativeTransport();
		case "ios":
			return iosTransport();
		case "android":
			return channelTransport();
		case "iframe":
		case "widget":
			return parentTransport(embedOrigin, string);
		default:
			// Android's HephonBridge is the Flutter fallback's channel (same name and shape).
			return (
				flutterTransport() ??
				reactNativeTransport() ??
				iosTransport() ??
				parentTransport(embedOrigin, string)
			);
	}
}

/** Whether a host transport is around this page. */
export function hasHost(
	embed: EmbedTransport | undefined,
	embedOrigin: string | null,
): boolean {
	return transport(embed, embedOrigin) !== null;
}

/**
 * Sends one message to the host. False when there is no host to receive it. `string` posts
 * the JSON text to an iframe parent too (the legacy cart message).
 */
export function deliver(
	message: object,
	embed: EmbedTransport | undefined,
	embedOrigin: string | null,
	{ string = false }: { string?: boolean } = {},
): boolean {
	const send = transport(embed, embedOrigin, string);
	const { type, id } = message as { type?: unknown; id?: unknown };
	// A `log` message is the trace itself.
	if (type !== "log") {
		debugLog(
			send ? "->" : "-> no app to receive",
			type ?? "legacy cart message",
			id ?? "",
		);
	}
	send?.(message, JSON.stringify(message));
	return send !== null;
}

/** A host message, validated; null for anything else (wrong shape, other versions). */
export function parseHostMessage(raw: unknown): HostToPage | null {
	let value = raw;
	if (typeof raw === "string") {
		try {
			value = JSON.parse(raw);
		} catch {
			return null;
		}
	}
	const parsed = hostToPageSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}

/** A basket call's diff, as tool outputs and basket routes return it (ADR-6). */
export interface BasketDiff {
	basket: { storeExternalId: string; currency: string };
	changes?: Pick<
		BasketChange,
		| "externalId"
		| "itemId"
		| "title"
		| "imageUrl"
		| "quantity"
		| "previousQuantity"
		| "unitPrice"
		| "substitution"
		| "eventId"
	>[];
	cleared?: boolean;
}

/**
 * The cart events of one basket call: one per change, or one `cart.cleared`. Keyed by the
 * server's event id, so a host that also gets the webhook can tell they are the same.
 */
export function cartMessages(
	diff: BasketDiff,
	context: { conversationId: string; origin: "agent" | "shopper" | "host" },
): PageToHost[] {
	const changes = diff.changes ?? [];
	if (changes.length === 0) {
		return [];
	}
	const storeId = diff.basket.storeExternalId;
	const { currency } = diff.basket;
	if (diff.cleared) {
		return [
			envelope(
				"cart.cleared",
				{
					storeId,
					conversationId: context.conversationId,
					removed: changes.map((change) =>
						cartEventItem(change, currency),
					),
				},
				changes[0]?.eventId,
			),
		];
	}
	return changes.map((change) =>
		envelope(
			cartEventType(change),
			{
				storeId,
				conversationId: context.conversationId,
				origin: context.origin,
				item: cartEventItem(change, currency),
			},
			change.eventId,
		),
	);
}

/**
 * The cart message hosts built for before bridge v1. Posted next to `checkout` for one
 * release, in its old shape with the retailer's ids; removed in the next release, not before
 * 2026-11-16 (runbook section 8).
 */
export function legacyCartMessage(cart: CartHandoff) {
	return {
		type: "hephon-commerce:cart",
		cart: {
			storeId: cart.storeId,
			currency: cart.currency,
			// Hosts from before v1 knew one id per product: the catalog id.
			items: cart.items.map((item) => ({
				productId: item.catalogId,
				quantity: item.quantity,
			})),
			total: cart.total,
		},
	};
}
